/**
 * The availability answer's rows (approved 10/2): RM writes the answer, with
 * one token per restaurant,
 *
 *   {{availability-row:<base64url JSON>}}
 *
 * and the app draws each one as the AI-chat restaurant row. This file reads a
 * token; lib/agent/availabilityReply.js on the server writes it (and turns it
 * into text lines for a text or an email). The two must agree field for field:
 * the server and client tests share one golden token.
 *
 * Every field is checked here too: a token that doesn't read is not drawn at
 * all — never shown as raw text — and only an http(s) link is ever a link.
 */

export const AVAILABILITY_ROW_TOKEN_RE = /\{\{availability-row:([A-Za-z0-9_-]{1,8000})\}\}/g

// A token still arriving while the reply streams: hidden until it is whole.
export const PARTIAL_AVAILABILITY_ROW_RE = /\{\{availability-row:[A-Za-z0-9_-]*\}?$/

const TOKEN_VERSION = 1
const LIMITS = { name: 200, line: 600, lines: 6, times: 12, url: 2048, label: 160 }
const DISPLAY_TIME_RE = /^(1[0-2]|[1-9]):[0-5]\d[ap]m$/
const PROFILE_ID_RE = /^[A-Za-z0-9_-]{1,120}$/
const KINDS = ['book', 'site', 'direct', 'notOnRm']

// The app's wording when a token carries none (the replies table's defaults).
export const DEFAULT_LINK_WORDING = {
  book: 'View/book:',
  site: 'View/book:',
  direct: 'Please view here directly:',
  notOnRm: 'Here is a link to their website to check availability directly:',
}

function cleanLine(value, max = LIMITS.line) {
  return String(value == null ? '' : value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F\u2028\u2029]+/g, ' ')
    .replace(/[{}]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .slice(0, max)
}

export function safeUrl(value) {
  const raw = String(value == null ? '' : value).trim()
  // eslint-disable-next-line no-control-regex
  if (!raw || /[\s\u0000-\u001F\u007F]/.test(raw)) return null
  const s = raw.replace(/[<>"'`{}|\\^]/g, ch => `%${ch.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`)
  if (s.length > LIMITS.url) return null
  let parsed
  try {
    parsed = new URL(s)
  } catch (err) {
    return null
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null

  return s
}

function base64UrlToUtf8(payload) {
  const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0))

  return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
}

/** The checked row a decoded token carries, or null. */
export function normalizeAvailabilityRow(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  if (raw.v !== TOKEN_VERSION) return null
  const name = cleanLine(raw.n, LIMITS.name)
  if (!name) return null
  const profileId = typeof raw.p === 'string' && PROFILE_ID_RE.test(raw.p) ? raw.p : null
  const times = Array.isArray(raw.t)
    ? raw.t.filter(x => typeof x === 'string' && DISPLAY_TIME_RE.test(x)).slice(0, LIMITS.times)
    : []
  const lines = Array.isArray(raw.x)
    ? raw.x.map(x => (typeof x === 'string' ? cleanLine(x) : '')).filter(Boolean).slice(0, LIMITS.lines)
    : []
  const url = safeUrl(raw.u)
  const kind = KINDS.includes(raw.k) ? raw.k : 'book'
  const count = Number.isInteger(raw.c) && raw.c > 0 && raw.c < 1e6 ? raw.c : 0

  return {
    name,
    profileId,
    times,
    lines,
    kind,
    url,
    linkLabel: url ? cleanLine(raw.l, LIMITS.label) || 'Link' : '',
    appLabel: url ? cleanLine(raw.b, LIMITS.label) || DEFAULT_LINK_WORDING[kind] : '',
    networkCount: profileId ? count : 0,
  }
}

/** A token's payload → its row, or null when it doesn't read. */
export function decodeAvailabilityRow(payload) {
  if (typeof payload !== 'string' || !/^[A-Za-z0-9_-]{1,8000}$/.test(payload)) return null
  try {
    return normalizeAvailabilityRow(JSON.parse(base64UrlToUtf8(payload)))
  } catch (err) {
    return null
  }
}

/** The row as plain lines, for "copy reply": what the member reads, the link spelled out. */
export function availabilityRowPlainText(row) {
  if (!row) return ''
  const lines = [row.name]
  if (row.times.length) lines.push(row.times.join(', '))
  lines.push(...row.lines)
  if (row.url) lines.push(`${row.appLabel} ${row.linkLabel} (${row.url})`)

  return lines.filter(Boolean).join('\n')
}

/**
 * What AgentRestaurantRow is given for an availability row: the restaurant's
 * card data with its group and Preferred left off (the approved availability
 * layout hides both; the row itself is not forked). The card stays whole
 * underneath (`raw`), so the heart, the count, the photos and the profile the
 * row opens are the restaurant's own.
 */
export function availabilityRowItem(card) {
  if (!card) return null

  return { raw: card.raw ?? card, group: '', status: '' }
}
